import { Link, useNavigate } from 'react-router-dom'
import { Archive, CheckCircle2, Circle, CornerDownRight, ExternalLink, Pause, SkipForward, Trash2, Undo2 } from 'lucide-react'
import type { Habit, HabitBoardCard } from '../../services/habitService'
import { habitState, hasSchedule } from '../../utils/habitSections'
import { scheduleWords, durationWords } from '../../utils/habitWords'
import { lifeAreaLabel } from '../../utils/taskForm'
import { ActionMenu } from '../ui/ActionMenu'
import { Timestamp } from '../ui/Timestamp'
import { Last14Strip } from './HabitKeepingUp'

/**
 * One row of the habit list (Habits UX pass 2026-09-06). A mark you can tap
 * to finish today, a title, the run, and one quiet line: when it comes
 * round (with its zone), how long, the last fourteen days as dots, what it
 * sits under. Everything else is under ⋯. Tone: "not today", never a miss.
 */
interface Props {
  habit: Habit
  card: HabitBoardCard | null | undefined
  showParent?: boolean
  showArea?: boolean
  timeHint?: 'created' | 'updated' | null
  onMark: (habit: Habit, outcome: 'done' | 'skipped' | 'pending') => void | Promise<void>
  onDelete: (habit: Habit) => void
  diaryMode?: boolean
}

export function HabitRow({ habit, card, showParent = false, showArea = false, timeHint = null, onMark, onDelete, diaryMode = false }: Props) {
  const navigate = useNavigate()
  const href = `/habits/${habit.id}`
  const state = habitState(habit)
  const archived = state.key === 'archived'
  const resting = state.key === 'paused' || state.key === 'parked'
  const live = !archived && !resting
  const todayOutcome = card?.today?.outcome || null
  const dueToday = live && todayOutcome === 'pending'
  const doneToday = live && todayOutcome === 'done'
  const skippedToday = live && todayOutcome === 'skipped'
  const area = habit.life_area || habit.parent?.life_area || null

  const menuItems = [
    { label: 'Open', icon: <ExternalLink className="h-4 w-4" />, onSelect: () => navigate(href), testId: 'habit-row-open' },
    ...(dueToday ? [{ label: 'Not today', icon: <SkipForward className="h-4 w-4" />, onSelect: () => void onMark(habit, 'skipped'), testId: 'habit-row-skip' }] : []),
    ...(doneToday || skippedToday ? [{ label: 'Take it back', icon: <Undo2 className="h-4 w-4" />, onSelect: () => void onMark(habit, 'pending'), testId: 'habit-row-undo' }] : []),
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => onDelete(habit), danger: true, testId: 'habit-row-delete' },
  ]

  const mark = live && !doneToday ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); void onMark(habit, 'done') }}
      aria-label={dueToday ? `Done today: ${habit.title}` : `Did it today: ${habit.title}`}
      title={dueToday ? 'Done today' : 'Did it today'}
      data-testid="habit-row-done"
      className={`group/mark inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-ok/10 hover:text-ok lg:h-8 lg:w-8 ${dueToday ? 'text-brand' : 'text-soft'}`}
    >
      <Circle className={`h-5 w-5 group-hover/mark:hidden ${dueToday ? '' : 'opacity-60'}`} aria-hidden="true" />
      <CheckCircle2 className="hidden h-5 w-5 group-hover/mark:block" aria-hidden="true" />
    </button>
  ) : (
    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center lg:h-8 lg:w-8" aria-hidden="true">
      {doneToday ? <CheckCircle2 className="h-5 w-5 text-ok" /> : archived ? <Archive className="h-4 w-4 text-soft" /> : <Pause className="h-4 w-4 text-muted" />}
    </span>
  )

  const when = (() => {
    if (!live) return null
    // Today's instants are clocks (with their zone): "today at Sep 12, 2026,
    // 9:00 AM PDT" said the day twice (North Star rule 2, 2026-09-12).
    if (doneToday && card?.today?.at) return <span className="shrink-0 text-ok">done <Timestamp value={card.today.at} mode="time" /></span>
    if (skippedToday) return <span className="shrink-0">not today</span>
    if (dueToday && card?.today?.scheduled_at) return <span className="shrink-0 font-medium text-brand">today at <Timestamp value={card.today.scheduled_at} mode="time" /></span>
    if (dueToday) return <span className="shrink-0 font-medium text-brand">today</span>
    if (card?.next) return <span className="shrink-0">next <Timestamp value={card.next.at || `${card.next.date}T12:00:00`} mode={card.next.at ? 'datetime' : 'date'} /></span>
    if (!hasSchedule(habit)) return <span className="shrink-0 text-muted">No schedule yet</span>
    return null
  })()

  return (
    <div
      className={`group flex items-center gap-1.5 rounded-lg border px-1.5 py-1 transition-colors sm:gap-2 sm:px-2 ${
        diaryMode ? 'border-warn bg-warn/20 hover:bg-warn/30' : 'border-soft bg-surface hover:border-brand'
      } ${archived ? 'opacity-70' : ''}`}
      onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button,[role=menu],[role=dialog]')) navigate(href) }}
      data-testid="habit-row"
      data-state={state.key}
    >
      {mark}
      <div className="min-w-0 flex-1 py-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <Link to={href} onClick={(e) => e.stopPropagation()} className={`truncate font-semibold hover:text-brand ${live ? 'text-primary' : 'text-muted'} ${diaryMode ? 'font-handwritten text-base sm:text-lg' : 'text-app-body'}`} title={habit.description || undefined} data-testid="habit-row-title">
            {habit.title}
          </Link>
          {state.words && (
            <span className="hidden shrink-0 rounded bg-surface-alt px-1.5 py-0.5 text-app-micro font-medium text-secondary sm:inline" data-testid="habit-row-state">
              {state.words}{state.key === 'paused' && state.pausedUntil ? <> until <Timestamp value={state.pausedUntil} mode="date" /></> : null}
            </span>
          )}
          {live && card && card.streak >= 2 && <span className="shrink-0 text-app-micro font-medium text-ok" data-testid="habit-row-streak">{card.streak} in a row</span>}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-x-2 overflow-hidden whitespace-nowrap text-app-micro text-muted sm:gap-x-2.5" data-testid="habit-row-meta">
          {state.words && <span className="shrink-0 sm:hidden">{state.words}</span>}
          {when}
          {hasSchedule(habit) && !dueToday && !doneToday && <span className="shrink-0">{scheduleWords(habit)}</span>}
          {habit.duration_minutes ? <span className="hidden shrink-0 sm:inline">{durationWords(habit.duration_minutes)}</span> : null}
          {live && card && card.chances_30 > 0 && <span className="hidden shrink-0 md:inline-flex"><Last14Strip days={card.last_14} size="sm" /></span>}
          {showParent && habit.parent?.title && (
            <span className="hidden min-w-0 items-center gap-1 truncate md:inline-flex" title={habit.parent.title}><CornerDownRight className="h-3 w-3 shrink-0" aria-hidden="true" />{habit.parent.title}</span>
          )}
          {showArea && area && <span className="hidden shrink-0 md:inline">{lifeAreaLabel(area)}</span>}
          {timeHint && <span className="shrink-0" data-testid="habit-row-time-hint">{timeHint === 'created' ? 'added' : 'updated'} <Timestamp value={timeHint === 'created' ? (habit.created_at || habit.updated_at) : (habit.updated_at || habit.created_at)} mode="datetime" /></span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {live && !hasSchedule(habit) && (
          <Link to={href} onClick={(e) => e.stopPropagation()} className="hidden rounded-md px-2 py-1 text-app-micro font-medium text-brand hover:bg-brand-subtle lg:inline-flex" data-testid="habit-row-set-schedule">Set a schedule</Link>
        )}
        <ActionMenu compact label={`More for ${habit.title}`} items={menuItems} testId="habit-row-more" />
      </div>
    </div>
  )
}

export default HabitRow
